import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { DollarOutlined } from '@ant-design/icons'
import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import {
  AppConfigurationKeys,
  Attachment,
  CoverageProviderByMonthResponse,
  DuplicateExpense,
  ExpenseForSaveResponse,
  FamilyMember,
  Feature,
  MakeExpenseIneligibleRequest,
  SaveExpenseRequest,
  ServiceProviderResponse,
  getAppConfiguration,
  getCoverageProviderByMonth,
  getDuplicateExpense,
  getExpenseForSave,
  getFamilyMembersApi,
  getServiceProviders,
  makeIneligibleExpenseApi,
  saveExpense,
  uploadFile,
} from 'core'

import ConfirmModal from 'common/components/dataDisplay/ConfirmModal'

import {
  Button,
  Card,
  Col,
  DatePicker,
  DefaultInput,
  DefaultRadioGroupV2,
  Icon,
  Label,
  Notification,
  Radio,
  RedAsterisk,
  Row,
  Skeleton,
  TextArea,
  Tooltip,
} from 'common/components'
import { formatMonth, now, submitDateToServerFormat, toDateWithoutTimeZone } from 'common/dateFormatters'
import {
  useCommandRequestV2,
  useFeatureFlag,
  useLoginUserV2,
  useQueryParams,
  useQueryRequestV2,
  useSharedState,
} from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'
import { UploadFileProps } from 'common/uploadFile'

import IndividualCoverageInformation from '../../../../common/components/benefit/Expenses/IndividualCoverageInformation'
import MecExplanation from '../../../../common/components/benefit/Expenses/MecExplanation'
import { disclaimerModal } from '../../DisclaimerModal'
import DocumentationForExpense from '../../DocumentationForExpense'
import SelectExpenseForFamilyMember from '../../SelectFamilyMember'
import Navigation from '../../navigation/Navigation'
import AddFamilyMemberModal from '../common/addFamilyMember/AddFamilyMemberModal'
import AddProviderModal from '../common/addProviderModal/AddProviderModal'
import CoverageProviderSelect from '../common/coverageProviderSelect'
import DuplicateExpenseModal from '../common/duplicateExpenseModal'
import ExpenseAttachmentUploader from '../common/expenseAttachmentUploader'
import SelectServiceProvider from '../common/selectServiceProvider/SelectServiceProvider'
import ExpenseTypeRadio from '../expenseTypeRadio'
import SmsStatusUpdateModal from './SmsStatusUpdateModal'

import './styles.less'

export const ME_VALUE: string = 'me'
export const ADD_FAMILY_MEMBER_VALUE: string = 'add'

const SubmitReimbursementRequest = () => {
  const history = useHistory()
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])
  const [hideExpensesSmsUpdates, setHideExpensesSmsUpdates] = useSharedState<boolean>(
    'HideExpensesSmsUpdates',
    false,
    true,
  )

  const params = useQueryParams()
  const expenseTypeId = +params.get('expenseTypeId')
  const planPeriodId = params.get('planPeriodId')
  const expenseId = params.get('expenseId') ?? null
  const { user, reloadUser } = useLoginUserV2()
  const canOptInForSmsOnExpense = useFeatureFlag(Feature.ExpensePremiumSubmissionSMSOptIn)

  const [hasDirtyData, setHasDirtyData] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [valuesToSubmit, setValuesToSubmit] = useState<SaveExpenseRequest | null>(null)
  const [duplicateExpense, setDuplicateExpense] = useState<DuplicateExpense | null>(null)

  const [addNewFamilyMemberVisible, setAddNewFamilyMemberVisible] = useState<boolean>(false)
  const [addNewProviderVisible, setAddNewProviderVisible] = useState<boolean>(false)
  const [confirmModalVisible, setConfirmModalVisible] = useState<boolean>(false)

  const [filesToBeUploaded, setFilesToBeUploaded] = useState<UploadFileProps[]>([])
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([])
  const [initialValues, setInitialValues] = useState<FormData | null>(null)
  const [providers, setProviders] = useState([])
  const [familyMemberName, setFamilyMemberName] = useState<string | null>(null)
  const [showSmsStatusModal, setShowSmsStatusModal] = useState<boolean>(false)

  const chooseExpenseTypeUrl = `${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_GET_REIMBURSED}/${planPeriodId}`

  const { data: expenseForSave } = useQueryRequestV2({
    queryFn: () => getExpenseForSave(planPeriodId, expenseTypeId, expenseId),
  })

  const { data: familyMembers, reload: reloadFamilyMembers } = useQueryRequestV2<FamilyMember[]>({
    queryFn: () => getFamilyMembersApi(),
  })

  const { sendQuery: sendGetCoverageProviderByMonth } = useQueryRequestV2<CoverageProviderByMonthResponse>()

  const { sendQuery: sendGetDuplicateExpenseQuery } = useQueryRequestV2<DuplicateExpense>()

  const { sendCommand: sendSaveExpenseCommand } = useCommandRequestV2((valuesToSubmit: SaveExpenseRequest) =>
    saveExpense(valuesToSubmit),
  )
  const { sendCommand: sendUploadFile } = useCommandRequestV2((file: File) => uploadFile(file))

  const { sendCommand: sendMakeIneligibleExpense, isLoading: isMakeIneligibleExpenseLoading } = useCommandRequestV2(
    (request: MakeExpenseIneligibleRequest) => makeIneligibleExpenseApi(request),
  )

  const { data: serviceProviders, reload: reloadServiceProviders } = useQueryRequestV2<ServiceProviderResponse[]>({
    queryFn: () => getServiceProviders(),
  })

  const { data: docReviewTurnAroundTime } = useQueryRequestV2({
    queryFn: () => getAppConfiguration(AppConfigurationKeys.DocReviewTurnAroundInformation),
  })

  useEffect(() => {
    if (serviceProviders) {
      setProviders(serviceProviders.map((provider) => provider.name))
    }
  }, [serviceProviders])

  useEffect(() => {
    if (expenseForSave) {
      setInitialValues({
        ...expenseForSave,
        multipleDates: expenseForSave.expenseId
          ? [
              {
                requestedAmount: expenseForSave.requestedAmount,
                purchasedDate: expenseForSave.purchasedDate,
              },
            ]
          : [{}],
        hasMultipleDates: false,
        familyMemberId: expenseForSave.expenseId ? expenseForSave.familyMemberId ?? ME_VALUE : undefined,
        mec: expenseForSave.useMecAttestation
          ? expenseForSave.purchasedDate && expenseForSave.mecCoverageProvider
            ? true
            : undefined
          : false,

        mecCoverageProvider: expenseForSave.mecCoverageProvider ? expenseForSave.mecCoverageProvider?.id : null,
        coveredByAnIndividualHealthInsurancePolicy: !!expenseForSave.expenseId,
      })

      setSelectedAttachments(expenseForSave.attachments)
      if (expenseForSave.expenseId) {
        setFamilyMemberName(expenseForSave.expenseFor)
      }
    }
  }, [expenseForSave])

  const disclaimer = () => {
    disclaimerModal(() => history.push(chooseExpenseTypeUrl), translate)
  }

  const onNameChange = (value: string, formik: any) => {
    if (value === ADD_FAMILY_MEMBER_VALUE) {
      setAddNewProviderVisible(true)
    }
  }

  const addProviderToList = (name: string) => {
    setProviders([name, ...providers])
  }

  const handleSubmit = async (valuesToSubmit: SaveExpenseRequest) => {
    await Promise.all(
      filesToBeUploaded.map(async (uploadItem) => {
        const result = await sendUploadFile(uploadItem.file)
        if (result.completed) {
          valuesToSubmit.attachments.push({
            filename: result.data.fileId,
            reuseInOtherExpenses: uploadItem.saveForLater ?? false,
            title: uploadItem.file.name,
          })
        }
      }),
    ).then(async () => {
      const result = await sendSaveExpenseCommand(valuesToSubmit)
      setIsSubmitting(false)
      if (result.completed) {
        valuesToSubmit.multipleDates.map((item) =>
          Notification.open({
            type: 'success',
            message: translate('get-reimburse.request.expense-added-successfully-message').replace(
              '[TURN_AROUND_TIME]',
              docReviewTurnAroundTime?.value,
            ),
          }),
        )
        reloadUser()
        setIsSubmitting(false)
        history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD)
      }
    })
  }

  const onSubmitClick = async (formikValues: any) => {
    setIsSubmitting(true)

    const values: SaveExpenseRequest = {
      ...formikValues,
      expenseId: expenseId,
      familyMemberId: formikValues.familyMemberId === ME_VALUE ? undefined : formikValues.familyMemberId,
      planPeriodId: planPeriodId,
      expenseTypeId: expenseTypeId,
      attachments: selectedAttachments.map((attachment) => ({
        id: attachment.attachmentId,
        filename: attachment.title,
        reuseInOtherExpenses: attachment.reuseInOtherExpenses,
        title: attachment.title,
      })),
    }

    setValuesToSubmit(values)

    if (values.multipleDates.length > 1) return await handleSubmit(values)

    const duplicateExpense = await sendGetDuplicateExpenseQuery(() =>
      getDuplicateExpense({
        expenseId: values.expenseId,
        familyMemberId: values.familyMemberId,
        purchasedDate: submitDateToServerFormat(values.multipleDates[0].purchasedDate),
        requestedAmount: values.multipleDates[0].requestedAmount,
      }),
    )

    if (duplicateExpense) {
      setDuplicateExpense(duplicateExpense)
    } else handleSubmit(values)
  }

  const onFamilySelectChange = async (value: string, formik: any) => {
    setAddNewFamilyMemberVisible(value === ADD_FAMILY_MEMBER_VALUE)
    setFamilyMemberName(
      value === ME_VALUE ? user?.firstName : familyMembers?.find((member) => member.id === value)?.firstName ?? null,
    )

    formik.setFieldValue('coveredByAnIndividualHealthInsurancePolicy', null)

    if (
      initialValues.useMecAttestation &&
      value !== ADD_FAMILY_MEMBER_VALUE &&
      formik.values.multipleDates &&
      formik.values.multipleDates.length > 0 &&
      formik.values.multipleDates[0].purchasedDate
    ) {
      const result = await sendGetCoverageProviderByMonth(() =>
        getCoverageProviderByMonth(
          submitDateToServerFormat(formik.values.multipleDates[0].purchasedDate),
          value !== ME_VALUE ? value : null,
        ),
      )

      if (result) {
        formik.setFieldValue('mecCoverageProvider', result.id)
        formik.setFieldValue('mec', true)
      } else {
        formik.setFieldValue('mecCoverageProvider', null)
        formik.setFieldValue('mec', undefined)
      }
    }
  }

  const handleExpenseMakeIneligible = async (expenseId: string) => {
    const result = await sendMakeIneligibleExpense({
      id: expenseId,
    })
    if (result.completed) {
      Notification.open({
        type: 'success',
        message: translate('get-reimburse.request.expense-updated-successfully-message'),
      })
      history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD)
    } else {
      Notification.open({
        type: 'error',
        message: translate('get-reimburse.errors.expense-can-not-be-updated'),
      })
    }
  }
  const areFilesToBeUploadedValid = () => {
    return filesToBeUploaded.length > 0 || selectedAttachments.length > 0
  }

  const canShowSmsStatusModal = !user.receiveSms && !hideExpensesSmsUpdates && canOptInForSmsOnExpense

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      validateOnChange
      onSubmit={() => {}}
      validate={(values) => {
        setHasDirtyData(true)
      }}
    >
      {(formik) => {
        return (
          <Navigation
            title={`${translate('get-reimburse.request.get-reimbursed-label')} ${
              expenseForSave?.expenseType?.name ?? ''
            }`}
            redirectPath={chooseExpenseTypeUrl}
            disclaimerModal={hasDirtyData ? disclaimer : undefined}
          >
            <Skeleton active loading={initialValues === null}>
              {initialValues && (
                <form onSubmit={formik.handleSubmit} autoComplete="off" className={'reimbursement_request-form'}>
                  <Row>
                    {/* Left Column */}
                    <Col md={24} lg={13}>
                      <Row>
                        {/* Expense Details Section */}
                        {formik.values?.requiresAttestationConfirmationToSubmitExpense && (
                          <Col span={24} className="bg-gray-2 p-24 mb-24">
                            <h5 className="section_title mb-32">Expense attestation</h5>

                            <Row className="field-row mb-24">
                              <Col md={24} xl={12}>
                                <SelectExpenseForFamilyMember
                                  inputName="familyMemberId"
                                  familyMembers={familyMembers}
                                  defaultValue={
                                    initialValues?.familyMemberId ? [initialValues?.familyMemberId] : undefined
                                  }
                                  onChange={(value) => onFamilySelectChange(value, formik)}
                                />
                              </Col>
                            </Row>

                            {familyMemberName && (
                              <Row className="field-row mb-24">
                                <Col md={24} xl={12}>
                                  <DefaultRadioGroupV2
                                    label={
                                      <>
                                        Was <b>{familyMemberName}</b> covered by a qualifying individual health plan on
                                        the date(s) this expense was incurred?
                                      </>
                                    }
                                    name={'coveredByAnIndividualHealthInsurancePolicy'}
                                    direction={'horizontal'}
                                    type={'radio'}
                                    items={[
                                      { name: 'Yes', value: true },
                                      { name: 'No', value: false },
                                    ]}
                                    onChange={(e) => {
                                      const value = e.target.value
                                      if (!value) setConfirmModalVisible(true)
                                    }}
                                  ></DefaultRadioGroupV2>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        )}

                        {(!formik.values?.requiresAttestationConfirmationToSubmitExpense ||
                          formik.values?.coveredByAnIndividualHealthInsurancePolicy) && (
                          <>
                            <Col span={24} className="bg-gray-2 p-24 mb-24">
                              <Row>
                                <Col span={24}>
                                  <h5 className="section_title mb-32">
                                    {translate('get-reimburse.request.expense-details-label')}
                                  </h5>
                                </Col>
                              </Row>
                              {!initialValues.requiresAttestationConfirmationToSubmitExpense && (
                                <Row className="field-row mb-24">
                                  <Col md={24} xl={12}>
                                    <SelectExpenseForFamilyMember
                                      inputName="familyMemberId"
                                      familyMembers={familyMembers}
                                      defaultValue={
                                        initialValues?.familyMemberId ? [initialValues?.familyMemberId] : undefined
                                      }
                                      onChange={(value) => onFamilySelectChange(value, formik)}
                                    />
                                  </Col>
                                </Row>
                              )}
                              <Row className="field-row mb-24">
                                <Col md={24} xl={12}>
                                  <SelectServiceProvider
                                    inputName="serviceProvider"
                                    providers={providers}
                                    defaultValue={
                                      initialValues?.serviceProvider ? [initialValues?.serviceProvider] : undefined
                                    }
                                    onChange={(value) => onNameChange(value, formik)}
                                  />
                                </Col>
                              </Row>
                              {!initialValues.expenseId && (
                                <Row className="field-row mb-16">
                                  <Col md={24} xl={12}>
                                    <Label>
                                      <RedAsterisk className={'mr-4'} />
                                      <span className={'mr-4'}>Does this expense span multiple dates?</span>
                                      <a
                                        href={
                                          'https://peoplekeephelpichra.zendesk.com/hc/en-us/articles/1500003007241-How-to-submit-a-reimbursement-request'
                                        }
                                        target={'_blank'}
                                        rel="noreferrer"
                                      >
                                        Learn more <Icon className="fal fa-external-link-alt" />
                                      </a>
                                    </Label>

                                    <Radio.Group
                                      name="hasMultipleDates"
                                      className=" pt-8"
                                      defaultValue={false}
                                      onChange={(e) => {
                                        const value = e.target.value
                                        if (!value) {
                                          formik.setFieldValue('multipleDates', [formik.values.multipleDates[0]])
                                        }
                                      }}
                                    >
                                      <Row>
                                        <Col span={8}>
                                          <Radio value={true} className="mr-8">
                                            Yes
                                          </Radio>
                                        </Col>

                                        <Col span={8}>
                                          <Radio value={false}>No</Radio>
                                        </Col>
                                      </Row>
                                    </Radio.Group>
                                  </Col>
                                </Row>
                              )}
                              <Card className="mb-16">
                                {formik.values &&
                                  formik.values.multipleDates.map((item: MultipleDatesAmount, index) => (
                                    <>
                                      <Row className="field-row mb-8 amount-dos-block" gutter={[16, 16]}>
                                        <Col md={12}>
                                          <DefaultInput
                                            defaultValue={item.requestedAmount?.toString()}
                                            showRequiredSign={true}
                                            label={translate('get-reimburse.request.expense-amount-label')}
                                            labelCustomStyle={'text-nowrap'}
                                            inputCustomStyle="mt-12"
                                            name={`multipleDates[${index}].requestedAmount`}
                                            prefix={<DollarOutlined />}
                                          />
                                        </Col>
                                        <Col md={12}>
                                          <Label className="text-nowrap mb-12">
                                            <RedAsterisk className={'mr-4'} />
                                            {translate('get-reimburse.request.date-of-service-label')}
                                          </Label>
                                          <Row className="flex w-100 mt-12">
                                            <Col span={formik.values.hasMultipleDates ? 20 : 24}>
                                              <DatePicker
                                                showRequiredSign={true}
                                                defaultValue={
                                                  item.purchasedDate ? toDateWithoutTimeZone(item.purchasedDate) : null
                                                }
                                                name={`multipleDates[${index}].purchasedDate`}
                                                id="purchasedDate"
                                                className="date_of_service_datepicker"
                                                placeholder={translate('get-reimburse.request.select-date-label')}
                                                allowClear={false}
                                                onChange={async (value) => {
                                                  if (initialValues.useMecAttestation) {
                                                    const multipleDates = formik.values.multipleDates.map((item, i) => {
                                                      if (i === index) {
                                                        return {
                                                          ...item,
                                                          purchasedDate: value,
                                                        }
                                                      }
                                                      return item
                                                    })
                                                    const result = await sendGetCoverageProviderByMonth(() =>
                                                      getCoverageProviderByMonth(
                                                        value,
                                                        formik.values.familyMemberId !== ME_VALUE
                                                          ? formik.values.familyMemberId
                                                          : null,
                                                      ),
                                                    )
                                                    if (result) {
                                                      formik.setValues({
                                                        ...formik.values,
                                                        multipleDates,
                                                        mecCoverageProvider: result.id,
                                                        mec: true,
                                                      })
                                                    } else {
                                                      formik.setValues({
                                                        ...formik.values,
                                                        multipleDates,
                                                        mecCoverageProvider: '',
                                                        mec: undefined,
                                                      })
                                                    }
                                                  }
                                                }}
                                                disabledDate={(d) =>
                                                  !d ||
                                                  d.isAfter(
                                                    toDateWithoutTimeZone(initialValues.maxPurchasedDate),
                                                    'day',
                                                  ) ||
                                                  d.isBefore(
                                                    toDateWithoutTimeZone(initialValues.minPurchasedDate),
                                                    'day',
                                                  ) ||
                                                  d.isAfter(now(), 'day')
                                                }
                                              />
                                            </Col>
                                            {formik.values.hasMultipleDates && (
                                              <Col span={4} className="flex flex__center">
                                                <Button
                                                  type="link"
                                                  danger
                                                  className="p-0 mb-8"
                                                  disabled={index === 0}
                                                  onClick={() => {
                                                    formik.setFieldValue(
                                                      'multipleDates',
                                                      formik.values.multipleDates.filter((_, i) => i !== index),
                                                    )
                                                  }}
                                                >
                                                  <Icon className="fal fa-trash-alt" />
                                                </Button>
                                              </Col>
                                            )}
                                          </Row>
                                        </Col>
                                      </Row>
                                    </>
                                  ))}
                                <Row className="field-row">
                                  {formik.values?.hasMultipleDates && (
                                    <Button
                                      onClick={() => {
                                        const result = [...formik.values.multipleDates, {}]
                                        formik.setFieldValue('multipleDates', result)
                                      }}
                                      icon="fal fa-plus"
                                    >
                                      Add new
                                    </Button>
                                  )}
                                </Row>
                              </Card>

                              <Row className="field-row">
                                <Col md={24} xl={12}>
                                  <TextArea
                                    label={translate('get-reimburse.request.expense-description-label')}
                                    inputStyles="mt-12"
                                    hint={translate('get-reimburse.request.expense-description-hint')}
                                    name="description"
                                    cols={16}
                                    placeholder={translate('get-reimburse.request.expense-description-placeholder')}
                                  />
                                </Col>
                              </Row>
                            </Col>
                            {/* End Expense Details Section */}

                            {/* Expense Receipt Section */}
                            <Col span={24} className="bg-gray-2 p-24 mb-24">
                              <Row>
                                <Col span={24}>
                                  <h5 className="section_title mb-32">
                                    {translate('get-reimburse.request.expense-reciept-label')}
                                  </h5>
                                </Col>
                              </Row>

                              <Row className="field-row">
                                <Col span={24}>
                                  <Row className={'mb-16'}>
                                    <Col span={24} className="expense_documentation_info">
                                      <h6 className="add_label mb-8">
                                        <RedAsterisk className={'mr-4'} />
                                        {translate('get-reimburse.request.add-documentation-label')}
                                      </h6>

                                      <DocumentationForExpense
                                        eligibility={initialValues?.expenseType.eligibility}
                                        companyName={initialValues?.companyName}
                                        translate={translate}
                                      />
                                    </Col>
                                  </Row>

                                  <Row>
                                    <Col span={24}>
                                      <ExpenseAttachmentUploader
                                        initialValues={selectedAttachments}
                                        onSelectedAttachmentsChanged={(newAttachments) =>
                                          setSelectedAttachments(newAttachments)
                                        }
                                        onUploadedFilesChanged={(files) => setFilesToBeUploaded(files)}
                                      />
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            </Col>
                          </>
                        )}
                        {/* End Expense Receipt Section */}
                      </Row>
                    </Col>
                    {/* End Left Column */}

                    {/* Right Column */}
                    {initialValues.useMecAttestation &&
                      formik.values?.multipleDates.length > 0 &&
                      formik.values?.multipleDates[0].purchasedDate && (
                        <Col md={24} lg={{ span: 10, offset: 1 }} xl={{ span: 9, offset: 2 }}>
                          <div className={'bg-gray-2 p-24'}>
                            <ExpenseTypeRadio
                              month={formatMonth(formik.values?.multipleDates[0].purchasedDate)}
                              yesText={translate('get-reimburse.premium-expense-form.minimum-coverage-yes')}
                              noText={translate('get-reimburse.premium-expense-form.minimum-coverage-no')}
                              mec={formik.values?.mec}
                              name={
                                formik.values?.familyMemberId === ME_VALUE ||
                                formik.values?.familyMemberId === undefined
                                  ? 'you'
                                  : familyMemberName
                              }
                            />

                            {formik.values?.mec !== undefined &&
                              formik.values?.mec &&
                              formik.values.multipleDates.length > 0 && (
                                <CoverageProviderSelect
                                  name={'mecCoverageProvider'}
                                  planPeriodId={planPeriodId}
                                  date={formik.values.multipleDates[0].purchasedDate}
                                  familyMemberId={
                                    formik.values.familyMemberId != ME_VALUE ? formik.values.familyMemberId : null
                                  }
                                />
                              )}
                          </div>

                          <MecExplanation />
                        </Col>
                      )}

                    {formik.values?.requiresAttestationConfirmationToSubmitExpense && (
                      <Col md={24} lg={{ span: 10, offset: 1 }} xl={{ span: 9, offset: 2 }}>
                        <IndividualCoverageInformation />
                      </Col>
                    )}
                    {/* End Right Column */}
                    {/* Bottom Actions Column */}
                    <Col span={24}>
                      <Row className="bottom-actions-row">
                        <Col span={24} className="bottom-actions pt-8 pb-8 pl-24 pr-24">
                          {formik.isValid && areFilesToBeUploadedValid() && (
                            <span className="mr-8 color-gray-7">{translate('get-reimburse.request.submit-info')}</span>
                          )}
                          <SubmitButton
                            onClick={() => {
                              if (canShowSmsStatusModal) setShowSmsStatusModal(true)
                              else onSubmitClick(formik.values)
                            }}
                            isValid={formik.isValid && areFilesToBeUploadedValid()}
                            loading={isSubmitting}
                          />
                        </Col>
                      </Row>
                    </Col>
                    {/* End Bottom Actions Column */}
                  </Row>
                </form>
              )}
            </Skeleton>

            <AddFamilyMemberModal
              visible={addNewFamilyMemberVisible}
              onCancel={() => setAddNewFamilyMemberVisible(false)}
              afterSuccessAdd={(familyMemberId: string, familyFirstName: string) => {
                reloadFamilyMembers().then(() => {
                  Notification.open({
                    type: 'success',
                    message: translate('get-reimburse.request.new-family-memeber-added'),
                  })
                  setAddNewFamilyMemberVisible(false)
                  setFamilyMemberName(familyFirstName)

                  formik.setValues({
                    ...formik.values,
                    coveredByAnIndividualHealthInsurancePolicy: null,
                    familyMemberId,
                  })
                })
              }}
            />

            <SmsStatusUpdateModal
              visible={showSmsStatusModal}
              defaultPhone={user.workPhone}
              onCancel={(rememberMe) => {
                onSubmitClick(formik.values)
                setShowSmsStatusModal(false)
                setHideExpensesSmsUpdates(rememberMe)
              }}
              onSubmit={async (phone) => {
                onSubmitClick({ ...formik.values, workPhone: phone }).then(() => {
                  pendo.track('SMS Status Update Modal')
                })
                setShowSmsStatusModal(false)
              }}
            />

            <AddProviderModal
              visible={addNewProviderVisible}
              onCancel={() => {
                setAddNewProviderVisible(false)
              }}
              onSubmitModal={(providerName: string) => {
                addProviderToList(providerName)
                setAddNewProviderVisible(false)
                // setFamilyMemberName(familyFirstName)

                formik.setFieldValue('serviceProvider', providerName)
              }}
            />

            <DuplicateExpenseModal
              duplicateExpense={duplicateExpense}
              visible={duplicateExpense !== null}
              onCancel={(cancelSubmit) => {
                setDuplicateExpense(null)
                cancelSubmit && setIsSubmitting(false)
              }}
              onOk={() => handleSubmit(valuesToSubmit).then(() => pendo.track('Duplicate Expense'))}
            />

            <ConfirmModal
              title="Expense is ineligible..."
              content={
                !!initialValues?.expenseId
                  ? 'This expense will be marked as "Ineligible" because the person was not covered by a qualifying individual health insurance plan at the time the expense was incurred.'
                  : 'For this expense to be eligible for reimbursement, the person who incurred the expense must have been covered by a qualifying individual health insurance plan at the time the expense was incurred.'
              }
              visible={confirmModalVisible}
              danger={!!initialValues?.expenseId}
              onCancel={() => {
                setConfirmModalVisible(false)
                formik.setFieldValue('coveredByAnIndividualHealthInsurancePolicy', null)
              }}
              okButtonLoading={isMakeIneligibleExpenseLoading}
              onConfirm={() => {
                if (!!initialValues?.expenseId) {
                  handleExpenseMakeIneligible(initialValues?.expenseId)
                } else history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD)
              }}
              buttonText={!!initialValues?.expenseId ? 'Confirm' : 'Confirm and exit'}
            />
          </Navigation>
        )
      }}
    </Formik>
  )
}

interface FormData extends Omit<ExpenseForSaveResponse, 'mecCoverageProvider'> {
  hasMultipleDates: boolean
  multipleDates: MultipleDatesAmount[]
  mec?: boolean
  mecCoverageProvider: string | number
  coveredByAnIndividualHealthInsurancePolicy?: boolean
}

type MultipleDatesAmount = {
  requestedAmount?: number
  purchasedDate?: string
}

export const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    familyMemberId: Yup.string()
      .nullable()
      .trim()
      .typeError(translate('translation:general.required'))
      .notOneOf([ADD_FAMILY_MEMBER_VALUE], translate('translation:general.required'))
      .required(translate('translation:general.required')),
    multipleDates: Yup.array().of(
      Yup.object().shape({
        requestedAmount: Yup.number()
          .nullable()
          .typeError(translate('get-reimburse.errors.only-numbers'))
          .min(0, translate('get-reimburse.errors.positive-numbers'))
          .required(translate('translation:general.required')),
        purchasedDate: Yup.date()
          .nullable()
          .typeError(translate('translation:general.required'))
          .required(translate('translation:general.required')),
      }),
    ),
    serviceProvider: Yup.string().nullable().trim().required(translate('translation:general.required')),
    description: Yup.string().nullable().trim(),
    mec: Yup.boolean().nullable().required(translate('translation:general.required')),
    mecCoverageProvider: Yup.string()
      .nullable()
      .when('mec', {
        is: (mec: boolean) => mec === true,
        then: Yup.string().required(translate('translation:general.required')),
      }),
    coveredByAnIndividualHealthInsurancePolicy: Yup.boolean()
      .nullable()
      .when('requiresAttestationConfirmationToSubmitExpense', {
        is: (requiresAttestationConfirmationToSubmitExpense: boolean) => requiresAttestationConfirmationToSubmitExpense,
        then: Yup.boolean().oneOf([true]).required(translate('translation:general.required')),
      }),
  })

type SubmitButtonProps = {
  isValid?: boolean
  loading?: boolean
  onClick?: () => void
}
export const SubmitButton = (props: SubmitButtonProps) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])

  const button = (
    <Button
      onClick={props.onClick}
      type="primary"
      htmlType="submit"
      disabled={!props.isValid || props.loading}
      loading={props.loading}
    >
      {translate('get-reimburse.request.submit-expense-label')}
    </Button>
  )

  if (!props.isValid) {
    return (
      <Tooltip title={translate('get-reimburse.request.next-tooltip')} position="left">
        {button}
      </Tooltip>
    )
  } else {
    return button
  }
}

export default SubmitReimbursementRequest
